import { appliesTo } from '../../audit/assignment';
import { PARTY_LABEL, windowDays } from '../../audit/requirements';
import { scoreTrend } from '../../audit/analytics';
import { TrendChart } from '../../ui/charts/TrendChart';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { canConductAudits, canSeeStore, equalsIgnoreCase } from '../../data/scope';
import { getAllStores } from '../../data/stores';
import {
  getSubmittedAuditsForStore,
  type AuditRecord,
} from '../../data/audits';
import { getTasksForStore, type ServerTask } from '../../data/tasks';
import {
  getStoreStatusesForStore,
  type StoreStatusRow,
} from '../../data/storeStatus';
import {
  getPublishedTemplates,
  type PublishedTemplate,
} from '../../data/templates';
import { getAllTemplateRows, templateNamesById, type TemplateRow } from '../../data/templateAdmin';
import { StoreVisitKpis } from '../analytics/VisitKpis';
import {
  computeWindowStatus,
  daysOverdue,
  daysUntil,
  WINDOW_LABELS,
} from '../../audit/windows';
import type { Store } from '../../data/types';
import { CompletedAuditRow } from '../../ui/audits/CompletedAuditRow';
import { TaskRow } from '../../ui/tasks/TaskRow';
import { CompleteTaskModal } from '../../ui/tasks/CompleteTaskModal';
import { longDistanceWindows } from '../../ui/longDistance';
import { ReopenTaskModal } from '../../ui/tasks/ReopenTaskModal';
import { DistrictLink } from '../../ui/entityLinks';
import { buildStoreRows, type StoreRow } from '../../data/dashboard';
import { GradeChip } from '../../ui/dashboard/DashboardTables';
import { TimelinessSplit } from '../../ui/dashboard/GradeExplain';
import { NO_GRADE } from '../../audit/grades';
import { formatDay } from '../../ui/dates';
import { reminderStatusText } from '../../data/reminderStatus';
import { getStoreReminders, type ReminderView } from '../../data/reminders';
import { dataViaApi } from '../../api/mode';
import { nextAuditSteps } from '../../data/reminderSchedule';
import { auditStepsText } from '../../ui/reminderText';
import { ReminderHistory } from '../../ui/ReminderHistory';
import { StoreBudgetsSection } from '../../ui/budgets/StoreBudgetsSection';
import { useOwnerFilter } from '../../ui/reports/OwnerFilter';
import { fitPageSize, isPhoneWidth, PagerBar, PagerTop, useFitPageSize, usePager } from '../../ui/Pager';
import { inDays, plural } from '../../ui/plural';

type TaskView = 'open' | 'completed';

/**
 * Everything about one store in one place: its audits and its tasks.
 * Reached by clicking a store name anywhere in the app.
 */
export function StoreDetailPage() {
  const { storeNumber } = useParams<{ storeNumber: string }>();
  const { user } = useAppUser();
  // The store's owner — staff only (2026-10-28; ownerOf is null for store teams).
  const ownerOf = useOwnerFilter().ownerOf;
  const [store, setStore] = useState<Store | null | 'missing'>(null);
  const [audits, setAudits] = useState<AuditRecord[] | null>(null);
  const [tasks, setTasks] = useState<ServerTask[] | null>(null);
  const [templates, setTemplates] = useState<PublishedTemplate[] | null>(null);
  /** Every template version's current name — audit cards on older versions keep their name. */
  const [templateNames, setTemplateNames] = useState<Map<number, string>>(new Map());
  const [templateRows, setTemplateRows] = useState<TemplateRow[]>([]);
  // null = loading. A failed status/template read leaves the table empty and
  // the grade "n/a" (never an inflated number).
  const [statusRows, setStatusRows] = useState<StoreStatusRow[] | null>(null);
  const [gradeInputFailed, setGradeInputFailed] = useState(false);
  const [taskView, setTaskView] = useState<TaskView>('open');
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<ServerTask | null>(null);
  const [reopening, setReopening] = useState<ServerTask | null>(null);

  useEffect(() => {
    if (!storeNumber) return;
    // includeInactive: past audits/tasks of a divested store stay reachable
    // via direct link; the page badges the store as inactive instead.
    getAllStores({ includeInactive: true })
      .then((all) => {
        const match = all.find((s) =>
          equalsIgnoreCase(s.storeNumber.trim(), storeNumber.trim()),
        );
        setStore(match ?? 'missing');
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [storeNumber]);

  const storeId = store && store !== 'missing' ? Number(store.id) : null;
  const accessible =
    store !== null && store !== 'missing' && user !== null && canSeeStore(user, store);

  // B5 (owner rulings 2026-10-25): who has been emailed about this store,
  // and the Notification rules' timing for each clock's next step. Server
  // mode only (null = hidden); a failed read never breaks the page.
  // Direct (rollback) mode: hidden from the start, no "Loading…" flash.
  const [reminderDays, setReminderDays] = useState<90 | 365>(90);
  const [reminders, setReminders] = useState<ReminderView | null | 'hidden'>(() => (dataViaApi() ? null : 'hidden'));
  const [remindersFailed, setRemindersFailed] = useState(false);
  // Another store: start again at 90 days (QA 2026-10-25).
  useEffect(() => {
    setReminderDays(90);
  }, [storeId]);
  useEffect(() => {
    if (storeId === null || !accessible || !dataViaApi()) return;
    let live = true;
    setRemindersFailed(false);
    setReminders(null); // loading — never the previous store's or range's list
    getStoreReminders(storeId, reminderDays)
      .then((v) => {
        if (live) setReminders(v ?? 'hidden');
      })
      .catch(() => {
        if (live) setRemindersFailed(true);
      });
    return () => {
      live = false;
    };
  }, [storeId, accessible, reminderDays]);
  const reminderView = reminders && reminders !== 'hidden' ? reminders : null;

  const refreshTasks = (id: number) => {
    getTasksForStore(id)
      .then(setTasks)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  };

  useEffect(() => {
    if (storeId === null || !accessible) return;
    // The page stays mounted when a link moves to another store: drop the
    // previous store's rows so its grade never mixes the two.
    setAudits(null);
    setTasks(null);
    setTemplates(null);
    setStatusRows(null);
    setGradeInputFailed(false);
    getSubmittedAuditsForStore(storeId)
      .then(setAudits)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
    getPublishedTemplates()
      .then(setTemplates)
      .catch(() => {
        setGradeInputFailed(true);
        setTemplates([]);
      });
    // Labels only — a failed read leaves the cards saying "Audit".
    getAllTemplateRows()
      .then((rows) => {
        setTemplateNames(templateNamesById(rows));
        setTemplateRows(rows);
      })
      .catch(() => undefined);
    getStoreStatusesForStore(storeId)
      .then(setStatusRows)
      .catch(() => {
        setGradeInputFailed(true);
        setStatusRows([]);
      });
    refreshTasks(storeId);
  }, [storeId, accessible]);

  const shownTasks = useMemo(() => {
    if (tasks === null) return null;
    const open = taskView === 'open';
    return tasks
      .filter((t) => (t.status === 'complete') !== open)
      .sort((a, b) =>
        open
          ? a.dueDate.localeCompare(b.dueDate)
          : (b.completedAt ?? '').localeCompare(a.completedAt ?? ''),
      );
  }, [tasks, taskView]);
  // A page at a time (owner ruling 2026-10-29: no section over ~80% of the screen — the next heading always shows).
  // Task cards are the tallest: at most 5 a page (3 on a phone) — owner test 2026-10-29; "View all" opens the Tasks page on this store.
  // On a phone a task card is ~300 px, so 2 a page (UI review V68: 3 made the section 123% of the screen).
  // UI review 3 (D4): cards with reopen quotes made 5 a page 86–110% of the window — the drawn cards are
  // measured and the page fits 80% (at most 5; heading, tabs and bar = chromePx).
  const [firstTaskGuess] = useState(() => (isPhoneWidth() ? 2 : fitPageSize(5)));
  const taskListRef = useRef<HTMLDivElement>(null);
  const taskPageSize = useFitPageSize(taskListRef, { initial: firstTaskGuess, chromePx: 170, phone: 2, max: 5 });
  const taskPager = usePager(shownTasks, `${storeId}|${taskView}`, taskPageSize);
  const auditPager = usePager(audits, storeId);

  /**
   * The store's grade, from the same rows as the home board. Tasks are this
   * store's own (the board can also count tasks assigned to this store's
   * account at stores the viewer can't see — not here).
   */
  const gradeRow = useMemo<StoreRow | null>(() => {
    if (!store || store === 'missing' || !templates || !statusRows || !audits || !tasks) return null;
    return (
      buildStoreRows({
        stores: [store],
        statuses: statusRows,
        templates,
        openTasks: tasks.filter((t) => t.status !== 'complete'),
        submittedAudits: audits,
      })[0] ?? null
    );
  }, [store, templates, statusRows, audits, tasks]);

  if (store === 'missing') {
    return (
      <div className="page-message">
        <p>No store with number “{storeNumber}” was found, or it isn’t one of your stores.</p>
        {/* Home — a store team may not use Audits much, and Home is everyone's start (UI review 3, S16). */}
        <Link to="/">Back to Home</Link>
      </div>
    );
  }
  if (error) {
    return <div className="page-message error">{error}</div>;
  }
  if (store === null || !user) {
    return <div className="page-message">Loading store…</div>;
  }
  if (!accessible) {
    return (
      <div className="page-message">
        <p>This store is outside your access.</p>
        <Link to="/">Back to Home</Link>
      </div>
    );
  }

  const canManage = canConductAudits(user.role);
  const openCount = (tasks ?? []).filter((t) => t.status !== 'complete').length;
  const completedCount = (tasks ?? []).length - openCount;

  return (
    <div className="store-page">
      <div className="page-head">
        <div>
          <h1>
            {store.storeNumber} — {store.name}
            {!store.active && (
              <span className="task-badge task-reopened">inactive</span>
            )}
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            <DistrictLink district={store.district} />
            {store.district.trim() && ' · '}
            {[store.address, store.city, store.state].filter(Boolean).join(', ')}
            {store.phone && ` · ${store.phone}`}
            {ownerOf && ownerOf(store) && ` · Owner: ${ownerOf(store)}`}
          </p>
          {/* Say what the tag means (DM + exec reflect 2026-10-25). */}
          {store.longDistance && (
            <p className="muted" style={{ margin: '0.15rem 0 0' }}>
              <span className="task-badge task-status-open">Long distance</span>{' '}
              {(templates && longDistanceWindows(templates.filter((t) => appliesTo(t.assignment, store)))) ||
                'Longer audit windows where a template sets one.'}
            </p>
          )}
        </div>
        <div className="dash-district-grade">
          Timeliness{' '}
          <GradeChip
            grade={gradeRow?.grade ?? NO_GRADE}
            kind="timeliness"
            state={gradeInputFailed ? 'unavailable' : gradeRow ? 'ready' : 'loading'}
            row={gradeRow ?? undefined}
          />{' '}
          Quality{' '}
          <GradeChip
            grade={gradeRow?.grade ?? NO_GRADE}
            kind="quality"
            state={gradeInputFailed ? 'unavailable' : gradeRow ? 'ready' : 'loading'}
            row={gradeRow ?? undefined}
          />
          {/* Whose lateness the Timeliness points went to, with the source behind each part (owner rulings 2026-10-28). */}
          {gradeRow && !gradeInputFailed && <TimelinessSplit row={gradeRow} />}
        </div>
      </div>

      <section className="store-section">
        <h2>Audit status</h2>
        {/* Wait for the clocks too — read as [] they showed every audit "Never
            audited" until they landed (QA 2026-10-25). */}
        {(templates === null || statusRows === null) && <p>Loading…</p>}
        {templates !== null && statusRows !== null && templates.length === 0 && (
          <p className="muted">No published audit templates yet.</p>
        )}
        {templates !== null && statusRows !== null && templates.length > 0 && (
          <div className="table-wrap board-wrap">
            {/* Phones: one card per audit (UI review U65 — the table hid Last completed / Next due off-screen). */}
            <table className="board-cards">
              <thead>
                <tr>
                  <th>Audit</th>
                  <th>Status</th>
                  <th>Last completed</th>
                  <th>Next due</th>
                </tr>
              </thead>
              <tbody>
                {templates
                  .filter((t) => appliesTo(t.assignment, store))
                  .flatMap((t) => t.requirements.map((req) => ({ t, req })))
                  .map(({ t, req }) => {
                  const row = statusRows.find((r) => r.templateId === t.id && r.party === req.party);
                  const now = new Date();
                  const status = computeWindowStatus(
                    {
                      lastCompleted: row?.lastCompletedDate ?? null,
                      nextDue: row?.nextDueDate ?? null,
                      frequencyDays: windowDays(req, store.longDistance),
                      dueSoonThresholdPct: t.dueSoonThresholdPct,
                    },
                    now,
                  );
                  return (
                    <tr key={`${t.id}-${req.party}`}>
                      <td className="card-full card-title">
                        {t.name}
                        <span className="muted cell-sub-inline">
                          {' '}
                          · {PARTY_LABEL[req.party]} every {plural(windowDays(req, store.longDistance), 'day')}
                          {store.longDistance && req.longDistanceDays && req.longDistanceDays !== req.frequencyDays
                            ? ` (long distance — instead of ${req.frequencyDays})`
                            : ''}
                        </span>
                      </td>
                      <td className="card-full">
                        <span className={`win-chip win-${status}`}>
                          {WINDOW_LABELS[status]}
                          {status === 'overdue' &&
                            row?.nextDueDate &&
                            ` ${plural(daysOverdue(row.nextDueDate, now), 'day')}`}
                        </span>
                        {/* What Flow 1 has sent for this clock (owner ruling 2026-10-22). */}
                        {row && reminderStatusText(status, row.lastNotifiedStatus ?? '', row.lastOverdueReminderAt ?? null, row.nextDueDate) && (
                          <span className="muted reminder-status">
                            {reminderStatusText(status, row.lastNotifiedStatus ?? '', row.lastOverdueReminderAt ?? null, row.nextDueDate)}
                          </span>
                        )}
                        {/* What Flow 1 sends next for this clock (B5). */}
                        {/* The flows act on active stores only. */}
                        {reminderView && row && store.active && (status === 'due_soon' || status === 'overdue') && (
                          <span className="muted reminder-next">
                            {reminderView.rules.paused
                              ? 'Notifications are paused'
                              : auditStepsText(
                                  nextAuditSteps(
                                    {
                                      status,
                                      nextDue: row.nextDueDate,
                                      lastNotifiedStatus: row.lastNotifiedStatus ?? '',
                                      lastOverdueReminderAt: row.lastOverdueReminderAt ?? null,
                                    },
                                    reminderView.rules,
                                    now,
                                  ),
                                  req.party,
                                  reminderView,
                                )}
                          </span>
                        )}
                      </td>
                      <td data-label="Last completed">
                        {row?.lastCompletedDate ? (
                          formatDay(row.lastCompletedDate)
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td data-label="Next due">
                        {row?.nextDueDate ? (
                          <>
                            {formatDay(row.nextDueDate)}
                            {status !== 'overdue' && (
                              <span className="muted">
                                {' '}
                                ({inDays(daysUntil(row.nextDueDate, now))})
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>


      <section className="store-section">
        {/* Tasks right under Audit status — the everyday part (owner ruling 2026-10-29); history and reminders sit at the bottom. */}
        <div className="page-head">
          <h2 style={{ margin: 0 }}>Tasks</h2>
          <div className="page-head-tools">
            <div className="seg-group" role="group" aria-label="Task status">
              <button
                className={taskView === 'open' ? 'seg seg-on' : 'seg'}
                onClick={() => setTaskView('open')}
              >
                Open ({openCount})
              </button>
              <button
                className={taskView === 'completed' ? 'seg seg-on' : 'seg'}
                onClick={() => setTaskView('completed')}
              >
                Completed ({completedCount})
              </button>
            </div>
            {/* Not for an inactive store: the Tasks page hides those (inactive stores are hidden by default). */}
            {store.active && shownTasks !== null && shownTasks.length > taskPager.items.length && (
              <Link className="btn btn-small" to={`/tasks?store=${encodeURIComponent(store.id)}&status=${taskView}`}>
                View all {shownTasks.length} →
              </Link>
            )}
          </div>
        </div>
        {shownTasks === null && <p>Loading…</p>}
        {shownTasks !== null && shownTasks.length === 0 && (
          <p className="muted">
            {taskView === 'open'
              ? 'No open tasks for this store. 🎉'
              : 'No completed tasks yet.'}
          </p>
        )}
        <PagerTop pager={taskPager} />
        <div ref={taskListRef} className="pager-list">
          {taskPager.items.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              store={store}
              view={taskView}
              canManage={canManage}
              onComplete={user.role === 'Executive' ? undefined : () => setCompleting(t)}
              onReopen={() => setReopening(t)}
            />
          ))}
        </div>
        <PagerBar pager={taskPager} noun="tasks" />
      </section>

      {/* Staff audits only — a store team's self-audit scores never feed trends or grades. */}
      {audits !== null && audits.filter((a) => a.score !== null && a.party !== 'store').length > 1 && (
        <TrendChart
          title="Score trend (average by month)"
          series={scoreTrend(audits.filter((a) => a.party !== 'store'), () => ({ key: 'store', label: store.name }))}
          colorKeys={['store']}
        />
      )}

      {audits !== null && <StoreVisitKpis audits={audits} templateRows={templateRows} />}

      {/* B8 (owner rulings 2026-10-25): the store's monthly KPI budgets. */}
      {templateRows.length > 0 && <StoreBudgetsSection store={store} templateRows={templateRows} />}

      <section className="store-section">
        <h2>Completed audits</h2>
        {audits === null && <p>Loading…</p>}
        {audits !== null && audits.length === 0 && (
          <p className="muted">No submitted audits for this store yet.</p>
        )}
        <PagerTop pager={auditPager} />
        {auditPager.items.map((a) => (
          <CompletedAuditRow
            key={a.id}
            audit={a}
            store={store}
            context="store"
            title={templateNames.get(a.templateId) ?? 'Audit'}
          />
        ))}
        <PagerBar pager={auditPager} noun="audits" />
      </section>

      {reminders !== 'hidden' && (
        <section className="dash-section">
          <h2>Reminders &amp; escalations</h2>
          <ReminderHistory
            entries={reminderView?.entries ?? null}
            failed={remindersFailed}
            linkTasks
            paged
            templateName={(id) => templates?.find((t) => t.id === id)?.name ?? ''}
            emptyText={`No reminders or escalations sent in the last ${reminderDays === 90 ? '90 days' : '12 months'}.`}
          />
          {reminderDays === 90 && reminderView && (
            <button type="button" className="btn btn-small" onClick={() => setReminderDays(365)}>
              Show the last 12 months
            </button>
          )}
        </section>
      )}

      {reopening && (
        <ReopenTaskModal
          task={reopening}
          onClose={() => setReopening(null)}
          onDone={() => {
            setReopening(null);
            if (storeId !== null) refreshTasks(storeId);
          }}
        />
      )}
      {completing && (
        <CompleteTaskModal
          task={completing}
          onClose={() => setCompleting(null)}
          onDone={() => {
            setCompleting(null);
            if (storeId !== null) refreshTasks(storeId);
          }}
        />
      )}
    </div>
  );
}
